import { useMemo } from 'react';

import { DateCell, DateCellAction, DropdownOption } from '@equip.health/ui';

import { toLocalMidnight, toUTCMidnight } from '~/lib/util/date.util';

type InquiryDateCellType = 'nextContactDate' | 'lastContactDate';

interface InquiryDateCellProps {
  cellType: InquiryDateCellType;
  communicationOptions: DropdownOption[];
  dateString?: string;
  inquiryId: string;
  isPatientPrimaryContact: boolean;
  patientId?: string;
  updateNextContactDate: (
    inquiryId: string,
    date: Date,
    version: string,
  ) => void;
  AddNote: (
    comment: string,
    contactMethod: string,
    inquiryId: string,
    isPatientPrimaryContact: boolean,
    patientId: string,
  ) => void;
  version: string;
}

const ActionTypeMapper: Record<InquiryDateCellType, DateCellAction> = {
  lastContactDate: DateCellAction.ADD_NOTE,
  nextContactDate: DateCellAction.CHANGE_DATE,
};

const InquiryDateCell = ({
  cellType,
  communicationOptions,
  dateString,
  inquiryId,
  isPatientPrimaryContact,
  patientId,
  updateNextContactDate,
  AddNote,
  version,
}: InquiryDateCellProps) => {
  const dateValue: Date = useMemo<Date>(() => {
    let d: Date;
    if (dateString) {
      d = new Date(dateString);
      if (cellType === 'nextContactDate') {
        // Next contact date is returned as UTC midnight. We must convert to local midnight for display.
        d = toLocalMidnight(d);
      }
    }
    return d;
  }, [dateString]);

  const handleUpdateNextContactDate = (date: Date) => {
    updateNextContactDate(inquiryId, toUTCMidnight(date), version);
  };

  const handleAddNote = (comment: string, contactMethod: string) => {
    AddNote(
      comment,
      contactMethod,
      inquiryId,
      isPatientPrimaryContact,
      patientId,
    );
  };

  return (
    <DateCell
      actionType={ActionTypeMapper[cellType]}
      contactMethodOptions={communicationOptions}
      date={dateValue}
      id={`${cellType}__inquiry-${inquiryId}`}
      onClick={({ date, comment, contactMethod }) => {
        if (cellType === 'nextContactDate') {
          handleUpdateNextContactDate(date);
        }

        if (cellType === 'lastContactDate') {
          handleAddNote(comment, contactMethod);
        }
      }}
      shouldExcludePastDates
      shouldShowRelativeTime
    />
  );
};

export default InquiryDateCell;
